import { useEffect, useState } from 'react';
import { Link } from '../components/Link.tsx';
import { useCtx } from '../lib.ts';

interface VoidView {
  hours: number;
  total: number;
  settled: number;
  voided: number;
  categories: { category: string; n: number }[];
  cameras: { cameraId: string; name: string | null; city: string | null; countryCode: string | null; provider: string | null; settled: number; voided: number; categories: Record<string, number> }[];
  series: { t: string; settled: number; voided: number }[];
  reasons: { reason: string; n: number }[];
  recent: { gameRoundId: string; at: string; cameraId: string; city: string | null; countryCode: string | null; category: string; reason: string | null }[];
}

const COLORS: Record<string, string> = {
  'Camera turned / view changed': '#a78bfa',
  'Live counter restarted': '#4cc9f0',
  'Counting fell behind': '#ffb347',
  'Feed gap / timeline break': '#ff5a5f',
  'Frozen picture': '#7dd3fc',
  'Round media rejected': '#f97316',
  'Operator void': '#ffd21f',
  Review: '#3ddc84',
  Other: '#8b93a3',
};
const flag = (cc: string | null) => (cc && /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🌐');
const short = (id: string) => id.replace(/^argus:opencctv_/, '').replace(/^[^_]+_/, '');

/** Why live-camera rounds are refunded: groups, trend, the cameras behind them, and the latest cases. */
export function Voids() {
  const { backend } = useCtx();
  const [hours, setHours] = useState(24);
  const [v, setV] = useState<VoidView | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(
      () =>
        void backend.server?.get<VoidView>(`/v1/admin/insights/voids?hours=${hours}`).then((r) => {
          if (r.ok) setV(r.value);
          else setError(r.error);
        }),
      0,
    );
    return () => clearTimeout(t);
  }, [backend, hours]);
  if (!backend.server) return <p className="empty">Void analysis needs the UrbanRats backend.</p>;
  if (!v) return <p className="empty">{error ?? 'Loading…'}</p>;
  const rate = v.total ? (100 * v.voided) / v.total : 0;
  const maxBar = Math.max(1, ...v.series.map((s) => s.settled + s.voided));
  const top = v.categories[0];
  // Donut of categories: each segment's length and start along the circle.
  const R = 70;
  const C = 2 * Math.PI * R;
  const segs = v.categories.map((c, i) => {
    const len = (c.n / Math.max(1, v.voided)) * C;
    const start = v.categories.slice(0, i).reduce((a, x) => a + (x.n / Math.max(1, v.voided)) * C, 0);
    return { ...c, len, start };
  });
  return (
    <div className="voids" data-testid="void-analysis">
      <div className="cc-head">
        <div>
          <h1>Void analysis</h1>
          <p className="note">Live-camera rounds refunded instead of settled, and why. Fixing the top group gives the biggest gain.</p>
        </div>
        <div className="seg-ctl" role="group" aria-label="Range">
          {[24, 72, 168].map((h) => (
            <button key={h} type="button" className={h === hours ? 'on' : ''} onClick={() => setHours(h)}>
              {h === 24 ? '24 h' : h === 72 ? '3 days' : '7 days'}
            </button>
          ))}
        </div>
      </div>
      <div className="cc-kpis">
        <div className="tile">
          <span className="tile-label">Void rate</span>
          <strong className={`tile-value ${rate > 20 ? 'bad-text' : ''}`}>{rate.toFixed(1)} %</strong>
          <span className="tile-sub">
            {v.voided} of {v.total} finished rounds
          </span>
        </div>
        <div className="tile">
          <span className="tile-label">Settled</span>
          <strong className="tile-value">{v.settled}</strong>
          <span className="tile-sub">rounds paid out normally</span>
        </div>
        <div className="tile">
          <span className="tile-label">Top cause</span>
          <strong className="tile-value small-value">{top?.category ?? '—'}</strong>
          <span className="tile-sub">{top ? `${top.n} rounds · ${((100 * top.n) / Math.max(1, v.voided)).toFixed(0)} % of voids` : 'no voids'}</span>
        </div>
      </div>
      <div className="voids-grid">
        <section className="card-x">
          <header className="card-h">
            <h2>Causes</h2>
          </header>
          <div className="donut-wrap">
            <svg viewBox="0 0 200 200" className="donut" role="img" aria-label="Void causes">
              <circle cx="100" cy="100" r={R} className="donut-bg" />
              {segs.map((c) => (
                <circle key={c.category} cx="100" cy="100" r={R} stroke={COLORS[c.category] ?? '#8b93a3'} strokeDasharray={`${c.len} ${C - c.len}`} strokeDashoffset={-c.start} className="donut-seg" />
              ))}
              <text x="100" y="96" className="donut-n">
                {v.voided}
              </text>
              <text x="100" y="118" className="donut-l">
                voided
              </text>
            </svg>
            <ul className="legend-list">
              {v.categories.map((c) => (
                <li key={c.category}>
                  <i style={{ background: COLORS[c.category] ?? '#8b93a3' }} /> {c.category} <strong>{c.n}</strong>
                </li>
              ))}
              {v.categories.length === 0 && <li className="muted">No voids in this range.</li>}
            </ul>
          </div>
        </section>
        <section className="card-x">
          <header className="card-h">
            <h2>Per hour</h2>
            <span className="legend">
              <span className="lg lg-ready">
                <i /> settled
              </span>
              <span className="lg lg-on-air">
                <i /> voided
              </span>
            </span>
          </header>
          <div className="hour-bars" role="img" aria-label="Settled and voided rounds per hour">
            {v.series.map((s) => (
              <div key={s.t} className="hb" title={`${s.t.slice(5, 13)}h UTC · ${s.settled} settled · ${s.voided} voided`}>
                <span className="v" style={{ height: `${(100 * s.voided) / maxBar}%` }} />
                <span className="s" style={{ height: `${(100 * s.settled) / maxBar}%` }} />
              </div>
            ))}
          </div>
          <div className="phase-labels">
            <span>{v.series[0]?.t.slice(5, 13).replace('T', ' ')}h</span>
            <span>UTC</span>
            <span>{v.series.at(-1)?.t.slice(5, 13).replace('T', ' ')}h</span>
          </div>
        </section>
      </div>
      <section className="card-x">
        <header className="card-h">
          <h2>Cameras</h2>
          <span className="note">void rate per camera, coloured by cause</span>
        </header>
        <ul className="cam-void-list">
          {v.cameras.map((c) => {
            const n = c.settled + c.voided;
            return (
              <li key={c.cameraId}>
                <Link to={`/studio?camera=${encodeURIComponent(c.cameraId)}`} className="where">
                  <strong>
                    {flag(c.countryCode)} {c.city ?? '—'}
                  </strong>
                  <span className="muted">{c.name ?? short(c.cameraId)}</span>
                </Link>
                <span className="cv-bar" aria-label={`${c.voided} of ${n} voided`}>
                  {Object.entries(c.categories).map(([k, x]) => (
                    <span key={k} style={{ width: `${(100 * x) / n}%`, background: COLORS[k] ?? '#8b93a3' }} title={`${k}: ${x}`} />
                  ))}
                  <span className="ok" style={{ width: `${(100 * c.settled) / n}%` }} title={`settled: ${c.settled}`} />
                </span>
                <span className="cv-n">
                  {c.voided}/{n}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
      <div className="voids-grid">
        <section className="card-x">
          <header className="card-h">
            <h2>Most frequent reasons</h2>
          </header>
          <ul className="reason-list">
            {v.reasons.map((r) => (
              <li key={r.reason}>
                <strong>{r.n}×</strong> <span>{r.reason}</span>
              </li>
            ))}
          </ul>
        </section>
        <section className="card-x">
          <header className="card-h">
            <h2>Latest voids</h2>
          </header>
          <ul className="reason-list">
            {v.recent.map((r) => (
              <li key={r.gameRoundId}>
                <i className="dot" style={{ background: COLORS[r.category] ?? '#8b93a3' }} />
                <Link to={`/rounds/${encodeURIComponent(r.gameRoundId)}`} className="mono">
                  {r.gameRoundId}
                </Link>
                <span className="muted">
                  {flag(r.countryCode)} {r.city} · {r.at.slice(11, 16)} · {r.category}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
